import * as logClient from "@classdojo/log-client";
import { useWatch } from "@web-monorepo/hooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { ZendeskAPI } from "react-zendesk";
import { getSessionData } from "./useSession";
import useDecagonAuthTokenMutation from "./api/auth/useDecagonAuthTokenMutation";
import { metrics } from "#/src/utils/metrics";
import { subHours } from "#/src/utils/dates";
import { useManualFeatureSwitchValue } from "./useFeatureSwitches";
import { isProduction } from "#/src/utils/env";
import { IS_TEST_SERVER } from "#/src/constants/client";

function getDecagonApiHost(): string | null {
  if (IS_TEST_SERVER) return "tutor-test.classdojo.com";
  if (isProduction) return "tutor.classdojo.com";

  return Config.decagonApiHost;
}

function getDecagonToolBranch(): string | undefined {
  if (isProduction && !IS_TEST_SERVER) return undefined;

  return Config.decagonToolBranch ?? "main";
}

type ZendeskWidgetFields = {
  email?: string;
  name?: string;
};

const ticketIdByUserType = {
  parent: 24658308388109,
  teacher: 24314991618061,
};

export type WindowWithHelperAPIMethods = Window &
  typeof globalThis & {
    duet?: {
      setUserId: (userId: string) => void;
      setMetadata: (metadata: Record<string, unknown>) => void;
      open: () => void;
      unhide: () => void;
      hide: () => void;
      setEventListener: (callback: (event: { eventName: string }) => void) => void;
      setUserIdAuth: (signature: string, epoch: number) => void;
    };
  };

const windowWithDecagon = window as WindowWithHelperAPIMethods;
export const callDecagonMethod = (callback: () => void) => {
  // silent the error since we don't want to break the app if the method is not available
  // this error handling is only for the decagon widget
  try {
    if (windowWithDecagon.duet == null) {
      const interval = setInterval(() => {
        if (windowWithDecagon.duet != null) {
          callback();
          clearInterval(interval);
        }
      }, 100);
    } else {
      callback();
    }
    // eslint-disable-next-line no-catch-all/no-catch-all -- helper widget callback may throw arbitrary runtime errors; we want to swallow + report them all rather than break the host page
  } catch (e) {
    logClient.logMessage("ERROR", "marketplace.helperWidgetCallbackFailed", {
      site: "marketplace",
      errorName: e instanceof Error ? e.name : "unknown",
      errorMessage: e instanceof Error ? e.message : String(e),
    });
  }
};

const useHelperWidgetAPI = () => {
  const [chatInitiated, setChatInitiated] = useState(false);
  const sessionData = getSessionData();
  const { data: decagonForTutorsSwitch } = useManualFeatureSwitchValue("web_marketplace_decagon_for_tutors");
  const { data: decagonForParentsSwitch } = useManualFeatureSwitchValue("web_marketplace_decagon_for_parents");
  const isDecagonForTutorsEnabled = decagonForTutorsSwitch === "test";
  const isDecagonForParentsEnabled = decagonForParentsSwitch === "on";
  const isParent = !!sessionData && sessionData.roles.includes("parent") && isDecagonForParentsEnabled;
  const isTutor = !!sessionData && sessionData.roles.includes("teacher") && isDecagonForTutorsEnabled;
  const isDecagonWidget = !!sessionData && (isParent || isTutor);
  const decagonAuthTokenMutation = useDecagonAuthTokenMutation();
  const decagonIsAuthenticated = useRef<Date>();

  // we call the mutation directly to avoid adding the mutation to the dependency array
  const decagonAuthMutateCall = decagonAuthTokenMutation.mutate;

  const setDecagonUserIdAuth = useCallback(
    (userId: string) => {
      if (
        decagonIsAuthenticated.current &&
        decagonIsAuthenticated.current.getTime() > subHours(new Date(), 2).getTime()
      ) {
        return;
      }

      decagonAuthMutateCall(undefined, {
        onSuccess: ({ data }) => {
          callDecagonMethod(() => {
            windowWithDecagon.duet?.setUserId(userId);
            windowWithDecagon.duet?.setUserIdAuth(data.token, data.epoch);
            decagonIsAuthenticated.current = new Date();
          });
        },
        onError: () => {
          metrics.increment("marketplace.decagon.auth.failure");
        },
      });
    },
    [decagonAuthMutateCall],
  );

  const setWidgetUserData = useCallback(
    ({
      userId,
      userType,
      email,
      fullname,
      caller,
    }: {
      userId: string;
      userType: "parent" | "teacher";
      email: string;
      fullname: string;
      caller: string;
    }) => {
      if (!email) {
        logClient.logMessage("WARN", `Setting widget user data without email: ${email}.  Set by ${caller}`);
      }

      setDecagonUserIdAuth(userId);

      const apiHost = getDecagonApiHost();
      const toolBranch = getDecagonToolBranch();
      const testMode = !isProduction || IS_TEST_SERVER;
      callDecagonMethod(() =>
        windowWithDecagon.duet?.setMetadata({
          email,
          user_type: userType === "teacher" ? "tutor" : userType,
          ...(toolBranch && { _decagon_branch_name: toolBranch }),
          isProduction,
          testMode,
          ...(apiHost && { api_host: apiHost }),
        }),
      );

      ZendeskAPI("webWidget", "updateSettings", {
        webWidget: {
          contactForm: {
            ticketForms: [{ id: ticketIdByUserType[userType] }],
            fields: [
              { id: "email", prefill: { "*": email || "" } },
              { id: "name", prefill: { "*": fullname || "" } },
            ],
          },
        },
      });
    },
    [setDecagonUserIdAuth],
  );

  const setDecagonEventListener = useCallback(() => {
    callDecagonMethod(() =>
      windowWithDecagon.duet?.setEventListener((event) => {
        if (event.eventName === "DUET_INITIALIZE") {
          setChatInitiated(true);
        }
      }),
    );
  }, []);

  // Handle Decagon widget initialization when chat is initiated
  useWatch([chatInitiated, sessionData, isDecagonWidget, setDecagonUserIdAuth], () => {
    if (!chatInitiated || !sessionData) return;
    if (!isDecagonWidget) return;

    if (sessionData.user_details.email) {
      setDecagonUserIdAuth(sessionData.user_details.uid);
    }
  });

  const openWidget = useCallback(
    ({ email: _email, name: _name }: ZendeskWidgetFields) => {
      if (isDecagonWidget) {
        callDecagonMethod(() => windowWithDecagon.duet?.open());
      } else {
        ZendeskAPI("webWidget", "updateSettings", {
          webWidget: {
            contactForm: {
              fields: [
                { id: "email", prefill: { "*": _email || "" } },
                { id: "name", prefill: { "*": _name || "" } },
              ],
            },
          },
        });
        ZendeskAPI("webWidget", "open");
      }
    },
    [isDecagonWidget],
  );

  const showWidget = useCallback(() => {
    if (isDecagonWidget) {
      ZendeskAPI("webWidget", "hide");
      callDecagonMethod(() => windowWithDecagon.duet?.unhide());
    } else {
      callDecagonMethod(() => windowWithDecagon.duet?.hide());
      ZendeskAPI("webWidget", "show");
    }
  }, [isDecagonWidget]);

  const hideWidget = () => {
    callDecagonMethod(() => windowWithDecagon.duet?.hide());
    ZendeskAPI("webWidget", "hide");
  };

  return useMemo(
    () => ({ setWidgetUserData, openWidget, showWidget, hideWidget, setDecagonEventListener }),
    [openWidget, setDecagonEventListener, showWidget, setWidgetUserData],
  );
};

export default useHelperWidgetAPI;
